import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './auth';
import { explain } from './api';
import { hasPremiumAccess, type PremiumAccess, type PremiumConfig } from './premium';
import { traderService, traderResult } from './traders';
import { closeBrowserPremiumAlerts, showBrowserPremiumAlerts } from './browser-premium-alerts';

type Service = Awaited<ReturnType<typeof traderService>>;
type Success<T> = T extends { ok: infer U } ? U : never;
type Page = Success<Awaited<ReturnType<Service['premium_notification_feed']>>>;
export type PremiumNotification = Page['items'][number];
interface PremiumState {
  access: PremiumAccess | null; config: PremiumConfig | null; loading: boolean; error: string | null;
  refresh: () => Promise<void>; notifications: PremiumNotification[]; unread: number;
  hasMore: boolean; loadingMore: boolean; loadMore: () => Promise<void>; markRead: () => Promise<void>;
}
const Context = createContext<PremiumState | null>(null);
const merge = (first: PremiumNotification[], rest: PremiumNotification[], limit: number) => [...new Map([...rest, ...first].map(item => [item.id, item])).values()].sort((a, b) => a.id > b.id ? -1 : 1).slice(0, limit);
export function PremiumProvider({ children }: { children: ReactNode }) {
  const auth = useAuth(), owner = auth.owner;
  const currentOwner = useRef(owner); currentOwner.current = owner;
  const [config, setConfig] = useState<PremiumConfig | null>(null);
  const [saved, setSaved] = useState<{ owner: string; access: PremiumAccess; feed: Page | null } | null>(null);
  const [loading, setLoading] = useState(false), [error, setError] = useState<string | null>(null), [loadingMore, setLoadingMore] = useState(false);
  const pollRef = useRef<((force?: boolean) => Promise<void>) | undefined>(undefined);
  const accessRef = useRef<PremiumAccess | null>(null), mutations = useRef(0);
  const state = saved?.owner === owner ? saved : null;
  accessRef.current = state?.access ?? null;
  const refresh = async () => { await pollRef.current?.(true); };
  useEffect(() => {
    let active = true, running = false, queued = false, queuedForce = false, first = true;
    let lastBalanceAt = 0, lastNotice: bigint | undefined;
    setSaved(null); setError(null); setLoading(Boolean(owner)); setLoadingMore(false);
    const captured = owner;
    const isCurrent = () => active && currentOwner.current === captured;
    const eligible = () => isCurrent() && hasPremiumAccess(accessRef.current);
    const poll = async (force = false) => {
      if (!isCurrent()) return;
      if (running) { queued = true; queuedForce ||= force; return; }
      running = true;
      const epoch = mutations.current;
      try {
        const identity = captured ? await auth.getIdentity() : undefined;
        if (!isCurrent() || (identity && identity.getPrincipal().toText() !== captured)) return;
        const service = await traderService(identity);
        const conf = await service.premium_config();
        if (!isCurrent()) return;
        setConfig(conf);
        if (!captured) return;
        const checkBalance = force || Date.now() - lastBalanceAt >= 30_000;
        const access = traderResult(await (checkBalance ? service.refresh_premium_access() : service.premium_status()));
        if (!isCurrent()) return;
        if (checkBalance) lastBalanceAt = Date.now();
        accessRef.current = access;
        if (!hasPremiumAccess(access)) {
          first = true; lastNotice = undefined;
          setSaved({ owner: captured, access, feed: null }); setError(null);
          await closeBrowserPremiumAlerts(captured);
          return;
        }
        const feed = traderResult(await service.premium_notification_feed([], 50n));
        const notices = [...feed.items], fresh = notices.filter(item => lastNotice === undefined || item.id > lastNotice);
        let cursor = feed.next_before[0];
        if (!first) while (cursor !== undefined && notices.length < Number(feed.retention_limit) && notices.length && (lastNotice === undefined || notices[notices.length - 1].id > lastNotice)) {
          const older = traderResult(await service.premium_notification_feed([cursor], 50n));
          if (!older.items.length) break;
          notices.push(...older.items); fresh.push(...older.items.filter(item => lastNotice === undefined || item.id > lastNotice)); cursor = older.next_before[0];
        }
        if (!isCurrent() || !hasPremiumAccess(access)) return;
        if (epoch !== mutations.current) { queued = true; return; }
        setSaved(previous => {
          const items = merge(notices, previous?.owner === captured ? previous.feed?.items ?? [] : [], Number(feed.retention_limit));
          return { owner: captured, access, feed: { ...feed, items: items.map(item => feed.unread_count === 0n ? { ...item, read: true } : item) } };
        });
        setError(null);
        const shouldAlert = !first; first = false;
        if (notices.length) lastNotice = notices.reduce((latest, item) => item.id > latest ? item.id : latest, lastNotice ?? 0n);
        if (shouldAlert && fresh.length) await showBrowserPremiumAlerts(captured, fresh, eligible);
      } catch (reason) {
        if (isCurrent()) {
          setError(explain(reason));
          // A second tab can revoke access during our inbox request. Treat any
          // failed poll as unverified rather than retaining a previously fresh UI.
          accessRef.current = null; setSaved(null); lastBalanceAt = 0;
          first = true; lastNotice = undefined;
          if (captured) await closeBrowserPremiumAlerts(captured).catch(() => undefined);
        }
      } finally {
        running = false;
        if (isCurrent()) setLoading(false);
        if (queued && isCurrent()) { const forceNext = queuedForce; queued = false; queuedForce = false; void poll(forceNext); }
      }
    };
    pollRef.current = poll; void poll(true);
    const interval = setInterval(() => void poll(), 15_000);
    const visible = () => { if (document.visibilityState === 'visible') void poll(); };
    const changed = () => void poll();
    document.addEventListener('visibilitychange', visible); window.addEventListener('premium-access-changed', changed);
    return () => { active = false; pollRef.current = undefined; clearInterval(interval); document.removeEventListener('visibilitychange', visible); window.removeEventListener('premium-access-changed', changed); if (captured) void closeBrowserPremiumAlerts(captured).catch(() => undefined); };
  }, [owner]);
  // Remove the visible inbox exactly when a verified balance lease expires.
  useEffect(() => {
    const expires = state?.access.expires_at[0];
    if (!owner || expires === undefined) return;
    const captured = owner;
    const timer = setTimeout(() => {
      if (currentOwner.current !== captured || hasPremiumAccess(accessRef.current)) return;
      setSaved(previous => previous?.owner === captured ? { ...previous, feed: null, access: { ...previous.access, eligible: false } } : previous);
      void closeBrowserPremiumAlerts(captured).catch(() => undefined);
    }, Math.max(0, Number(expires / 1_000_000n) - Date.now()) + 1);
    return () => clearTimeout(timer);
  }, [owner, state?.access.expires_at[0]]);
  async function markRead() {
    if (!owner || !state?.feed?.items.length) return;
    const captured = owner, upto = state.feed.items[0].id; mutations.current++;
    try {
      const identity = await auth.getIdentity(); if (currentOwner.current !== captured) return;
      traderResult(await (await traderService(identity)).mark_premium_notifications_read(upto));
      if (currentOwner.current === captured) setSaved(previous => previous?.owner === captured && previous.feed ? { ...previous, feed: { ...previous.feed, unread_count: BigInt(previous.feed.items.filter(item => !item.read && item.id > upto).length), items: previous.feed.items.map(item => item.id <= upto ? { ...item, read: true } : item) } } : previous);
    } catch (reason) { if (currentOwner.current === captured) setError(explain(reason)); }
    finally { if (currentOwner.current === captured) { mutations.current++; void pollRef.current?.(); } }
  }
  async function loadMore() {
    const feed = state?.feed;
    if (!owner || !feed?.items.length || loadingMore || !hasPremiumAccess(state?.access ?? null)) return;
    const captured = owner, before = feed.items[feed.items.length - 1].id, epoch = mutations.current; setLoadingMore(true);
    try {
      const identity = await auth.getIdentity(); if (currentOwner.current !== captured) return;
      const next = traderResult(await (await traderService(identity)).premium_notification_feed([before], 50n));
      if (currentOwner.current === captured && epoch === mutations.current && hasPremiumAccess(accessRef.current)) setSaved(previous => previous?.owner === captured && previous.feed ? { ...previous, feed: { ...next, items: merge(next.items, previous.feed.items, Number(next.retention_limit)) } } : previous);
    } catch (reason) { if (currentOwner.current === captured) setError(explain(reason)); }
    finally { if (currentOwner.current === captured) setLoadingMore(false); }
  }
  const feed = hasPremiumAccess(state?.access ?? null) ? state?.feed : null;
  return <Context.Provider value={{ access: state?.access ?? null, config, loading, error, refresh, notifications: feed?.items ?? [], unread: Number(feed?.unread_count ?? 0n), hasMore: Boolean(feed && feed.items.length < Number(feed.retained_count)), loadingMore, loadMore, markRead }}>{children}</Context.Provider>;
}
export function usePremium() { const value = useContext(Context); if (!value) throw Error('PremiumProvider is missing'); return value; }
